/**
 * Web push: make a closed/backgrounded tab a real failover surface. The web is a login-less peer, but it
 * already holds each pairing's `deviceRegToken` (mirrored in the pairing payload) — which IS the `proof` the
 * existing `registerDevice` function checks — so the web registers its FCM token as just another "device" of
 * the pairing. The server's chat-notify fanout then reaches it too; no backend change beyond CORS. Pushes are
 * content-free (see firebase-messaging-sw.js); the tab decrypts on open. No-ops until VAPID_KEY is set.
 */
import { getMessaging, getToken, isSupported } from "firebase/messaging";
import { app, VAPID_KEY } from "./firebase";
import type { PairingPayload } from "./pairings";

export type PushStatus = "unconfigured" | "unsupported" | "denied" | "registered";

/** This browser's FCM token once registered — sent along with a read so push targeting can promote it. */
let webFcmToken: string | undefined;

/**
 * Request notification permission (only when `interactive` — i.e. behind a user click; never prompt on load),
 * mint a web FCM token, and register it against every pairing so the agent's notifyChat reaches this browser.
 */
export async function enableWebPush(pairings: PairingPayload[], interactive: boolean): Promise<PushStatus> {
  if (!VAPID_KEY) return "unconfigured";
  if (!(await isSupported())) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  if (Notification.permission === "default") {
    if (!interactive) return "denied"; // don't prompt unprompted
    if ((await Notification.requestPermission()) !== "granted") return "denied";
  }
  const reg = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
  const token = await getToken(getMessaging(app), { vapidKey: VAPID_KEY, serviceWorkerRegistration: reg });
  if (!token) return "denied";
  webFcmToken = token;
  await Promise.all(
    pairings.map((p) =>
      fetch(`${p.functionsBaseUrl}/registerDevice`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pairingId: p.pairingId, fcmToken: token, proof: p.deviceRegToken, platform: "web" }),
      }).catch(() => undefined),
    ),
  );
  return "registered";
}

/**
 * Tell the user's other devices to drop a session's chat notification once this tab has displayed it. The
 * per-message read receipts already carry the read state, but a phone whose app isn't running can't observe
 * them, so its notification would linger — this withdraws it. Same device proof as registration; the payload
 * names only the session, never content. Best-effort: a failure just leaves the receipt path to catch up.
 */
export async function notifyRead(pairing: PairingPayload, sessionId: string, readUpTo: number): Promise<void> {
  await fetch(`${pairing.functionsBaseUrl}/notifyRead`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      pairingId: pairing.pairingId,
      sessionId,
      proof: pairing.deviceRegToken,
      readUpTo,
      ...(webFcmToken ? { fcmToken: webFcmToken } : {}),
    }),
  }).catch(() => undefined);
}
